'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { NoticeBanner } from '@/shared/ui';
import { useProductCheckout } from '@/features/payment/hooks/use-product-checkout';
import type { CheckoutDescriptor } from '@/types/checkout';

/**
 * Buy CTA for an account-level entitlement product — Tokens Pro Pass
 * (`kind="pass"`) and Pro Plan (`kind="subscription"`). Sibling of
 * {@link PackBuyCta} (which stays building-bound); both share the teal capsule
 * styling but this one drives {@link useProductCheckout} off the server
 * `checkout` descriptor, so it opens a real Paddle checkout in place instead of
 * routing the buyer to the mobile apps.
 *
 * When the descriptor is null (country not served on a web rail — RU/BY), it
 * renders the server `web_payment_notice` NoticeBanner instead of a dead button
 * (never the App Store / Google Play badges). The webhook owns the grant.
 */
export function PassBuyCta({
  descriptor,
  kind,
  sku,
  verbLabel,
  webPaymentNotice,
  calm = false,
  className,
  onStart,
  onPurchased,
  onClosed,
}: {
  /** Server-provided provider-agnostic checkout descriptor (`{provider, reference}`). */
  descriptor: CheckoutDescriptor | null | undefined;
  kind: 'pass' | 'subscription';
  /** Canonical `ai.getfacade.*` product id planted on the correlation intent. */
  sku: string;
  /** Verb CTA label ("Get Pro Pass" / "Get Pro Plan") — the price shows as a
   *  separate badge, so the button stays a clean action. */
  verbLabel: string;
  /** Server-driven regional advisory shown when checkout is unavailable. */
  webPaymentNotice?: string | null;
  /** Calmer, outlined capsule (secondary emphasis). */
  calm?: boolean;
  className?: string;
  /** Fired the instant the user taps buy (analytics — the "purchase started" hook). */
  onStart?: () => void;
  /** Fired on checkout.completed. */
  onPurchased?: () => void;
  /** Fired on checkout.closed (purchase OR cancel). */
  onClosed?: () => void;
}) {
  const t = useTranslations('packs');
  const { available, isLaunching, startCheckout } = useProductCheckout({
    descriptor,
    kind,
    sku,
    callbacks: { onPurchased, onClosed },
  });

  if (!available) {
    // Country not served on a web rail (or client not configured). Show the
    // server-localized notice when present; otherwise render nothing (the price
    // badge still communicates the product) — never the mobile store badges.
    return webPaymentNotice ? (
      <NoticeBanner
        variant="warning"
        message={webPaymentNotice}
        className={className}
      />
    ) : null;
  }

  return (
    <div className={cn('flex w-full flex-col items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => {
          onStart?.();
          void startCheckout();
        }}
        disabled={isLaunching}
        className={cn(
          // min-h + wrapping (not fixed height) so a long name + long currency
          // can break to a second line instead of clipping.
          'inline-flex min-h-11 items-center justify-center whitespace-normal rounded-full px-6 py-2 text-center text-list-item-value font-semibold transition-opacity hover:opacity-90 disabled:opacity-60',
          calm
            ? 'border border-primary text-primary hover:bg-primary/5'
            : 'bg-primary text-primary-foreground',
        )}
      >
        {isLaunching ? t('checkoutOpening') : verbLabel}
      </button>
    </div>
  );
}
